import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import { convertJsonToHtml } from "../utils/jsonToHtmlConverter";
import fdLogo from '../assets/images/logo-fisher-dugmore-financial.png';

// Constants
const VARIABLE_ELEMENT_MAP = {
  // Personal Information
  'first_name': 'text',
  'last_name': 'text',
  'full_name': 'text',
  'id_number': 'text',
  'email': 'email',
  'phone': 'tel',
  'address': 'textarea',
  'tax_number': 'text',
  'date_of_birth': 'date',
  
  // Entity Information
  'entity_name': 'text',
  'organization_legal_name': 'text',
  'entity_registration_number': 'text',
  'license_number': 'text',
  
  // Financial Advisor Information
  'branch': 'text',
  
  // Contract Information
  'contract_date': 'date',
  'start_date': 'date',
  'end_date': 'date',
  'signature_date': 'date',
  'duration': 'number',
  'initial_amount': 'number',
  'final_amount': 'number',
  'initial_due_date': 'date',
  'final_due_date': 'date',
  
  // Agreement fields
  'agreement': 'checkbox',
  'signature': 'text',
  
  // Service fields
  'service_type': 'select',
  'service_description': 'textarea',
  'special_requirements': 'textarea',
  'priority': 'select',
  
  // Generic fields
  'party_type': 'select',
  'notes': 'textarea',
  'comments': 'textarea',
  'description': 'textarea',
  'amount': 'number',
  'percentage': 'number',
  'quantity': 'number',
  'rate': 'number',
  'status': 'select',
  'type': 'select',
  'category': 'select',
  'title': 'text',
  'name': 'text',
  'value': 'text',
  'reference': 'text',
  'code': 'text',
  'number': 'number',
  'date': 'date',
  'time': 'time',
  'url': 'url',
  'website': 'url',
  'contact': 'text',
  'contact_person': 'text',
  'contact_number': 'tel',
  'contact_email': 'email',
  'postal_address': 'textarea',
  'physical_address': 'textarea',
  'billing_address': 'textarea',
  'delivery_address': 'textarea'
};

/**
 * Get placeholder data for variables (temporary until Supabase integration).
 * 
 * @param {string} variableName - Name of the variable
 * @returns {Promise<string>} Placeholder value for the variable
 */
const getPlaceholderData = async (variableName) => {
  // TODO: Replace with actual Supabase calls
  const placeholderValues = {
    // Personal Information
    'first_name': 'John',
    'last_name': 'Doe',
    'full_name': 'John Doe',
    'id_number': '8001015009083',
    'email': 'john.doe@email.com',
    'phone': '+27 82 123 4567',
    'address': '123 Main Street\nCape Town, 8001\nSouth Africa',
    'tax_number': 'TAX123456789',
    'date_of_birth': '1980-01-01',
    
    // Entity Information
    'entity_name': 'ABC Corporation',
    'organization_legal_name': 'ABC Corporation (Pty) Ltd',
    'entity_registration_number': 'REG123456789',
    'license_number': 'LIC987654321',
    
    // Financial Advisor Information
    'branch': 'Cape Town Branch',
    
    // Contract Information
    'contract_date': new Date().toISOString().split('T')[0],
    'start_date': new Date().toISOString().split('T')[0],
    'end_date': new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    'signature_date': new Date().toISOString().split('T')[0],
    'duration': '12',
    'initial_amount': '5000',
    'final_amount': '15000',
    'initial_due_date': new Date().toISOString().split('T')[0],
    'final_due_date': new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    
    // Service fields
    'service_type': 'Consulting Services',
    'service_description': 'Professional consulting services for business development and strategic planning.',
    'special_requirements': 'Monthly progress reports required.',
    'priority': 'High',
    
    // Generic defaults
    'party_type': 'Individual',
    'notes': 'Additional notes will be added here.',
    'comments': 'Comments section.',
    'description': 'Description will be provided.',
    'amount': '1000',
    'percentage': '10',
    'quantity': '1',
    'rate': '500',
    'status': 'Active',
    'type': 'Standard',
    'category': 'General',
    'title': 'Document Title',
    'name': 'Document Name',
    'value': 'Sample Value',
    'reference': 'REF001',
    'code': 'CODE123',
    'number': '001',
    'date': new Date().toISOString().split('T')[0],
    'time': new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }),
    'url': 'https://example.com',
    'website': 'https://company.com',
    'contact': 'Contact Person',
    'contact_person': 'Jane Smith',
    'contact_number': '+27 82 987 6543',
    'contact_email': 'contact@company.com',
    'postal_address': 'PO Box 123\nCape Town, 8000\nSouth Africa',
    'physical_address': '456 Business Street\nCape Town, 8001\nSouth Africa',
    'billing_address': '789 Finance Road\nCape Town, 8002\nSouth Africa',
    'delivery_address': '321 Delivery Avenue\nCape Town, 8003\nSouth Africa'
  };
  
  return placeholderValues[variableName] || `[${variableName}]`;
};

/**
 * Replace variables in JSON string with database data.
 * 
 * @param {string} jsonString - JSON string containing variables
 * @returns {Promise<string>} JSON string with variables replaced
 */
const replaceVariablesWithData = async (jsonString) => {
  // Find all variables in the format {{variable_name}}
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables = [...jsonString.matchAll(variableRegex)];
  
  let processedJson = jsonString;
  
  // Replace each variable with database data
  for (const variable of variables) {
    const fullMatch = variable[0]; // {{variable_name}}
    const variableName = variable[1].trim(); // variable_name
    
    // Get data from database (placeholder for now)
    const dbValue = await getPlaceholderData(variableName);
    
    // Replace the variable with the database value
    processedJson = processedJson.replace(new RegExp(fullMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), dbValue);
  }
  
  return processedJson;
};

/**
 * Generate HTML form from JSON data.
 * 
 * @param {Object} jsonData - JSON data containing form structure
 * @returns {string} HTML string for the form
 */
const generateFormHtml = (jsonData) => {
  // Handle both old format (sections) and new format (pages)
  let sections = [];
  if (jsonData.pages) {
    // New format: extract all sections from all pages
    sections = jsonData.pages.flatMap(page => page.sections || []);
  } else {
    // Old format: use sections directly
    sections = jsonData.sections || [];
  }
  
  // Find all variables in the JSON and create form inputs
  const jsonString = JSON.stringify(jsonData);
  const variableRegex = /\{\{([^}]+)\}\}/g;
  const variables = [...jsonString.matchAll(variableRegex)];
  
  let formHtml = '<div class="form-container" style="max-width: 800px; margin: 0 auto; padding: 20px;">';
  
  // Add form header
  formHtml += `
    <div style="text-align: center; margin-bottom: 30px;">
      <img src="${fdLogo}" alt="Fisher Dugmore Financial" style="width: 120px; margin-bottom: 20px;" />
      <h2 style="margin: 0; text-transform: uppercase; letter-spacing: 2px;">Template Form</h2>
    </div>
  `;
  
  // Create form inputs for each unique variable
  const uniqueVariables = [...new Set(variables.map(v => v[1].trim()))];
  
  uniqueVariables.forEach(variableName => {
    const elementType = VARIABLE_ELEMENT_MAP[variableName] || 'text';
    const displayName = variableName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    formHtml += `<div style="margin-bottom: 20px;">`;
    formHtml += `<label style="display: block; margin-bottom: 5px; font-weight: bold; color: #333;">${displayName}:</label>`;
    
    switch (elementType) {
      case 'textarea':
        formHtml += `<textarea name="${variableName}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-family: inherit; resize: vertical;" rows="3" placeholder="Enter ${displayName.toLowerCase()}..."></textarea>`;
        break;
      case 'select':
        formHtml += `<select name="${variableName}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-family: inherit;">
          <option value="">Select ${displayName}</option>
          <option value="option1">Option 1</option>
          <option value="option2">Option 2</option>
          <option value="option3">Option 3</option>
        </select>`;
        break;
      case 'checkbox':
        formHtml += `<label style="display: flex; align-items: center; font-weight: normal;">
          <input type="checkbox" name="${variableName}" style="margin-right: 8px;" />
          ${displayName}
        </label>`;
        break;
      default:
        formHtml += `<input type="${elementType}" name="${variableName}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-family: inherit;" placeholder="Enter ${displayName.toLowerCase()}..." />`;
    }
    
    formHtml += `</div>`;
  });
  
  formHtml += '</div>';
  
  return formHtml;
};

/**
 * TemplateViewModal component for viewing and editing templates.
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Function to close the modal
 * @param {Object} [props.template] - Template data to display
 */
function TemplateViewModal({ isOpen, onClose, template }) {
  const [activeTab, setActiveTab] = useState(0);
  const [processedJson, setProcessedJson] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [formHtml, setFormHtml] = useState("");
  const [formValues, setFormValues] = useState({});
  const [uniqueVariables, setUniqueVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && template) {
      processTemplate();
    }
  }, [isOpen, template]);

  // Extract unique variables from JSON
  const extractUniqueVariables = (jsonString) => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    return [...new Set([...jsonString.matchAll(variableRegex)].map(v => v[1].trim()))];
  };

  const processTemplate = async () => {
    setLoading(true);
    setError(null);
    try {
      let jsonString = "";
      if (template.json) {
        if (typeof template.json === 'string') {
          jsonString = template.json;
        } else if (typeof template.json === 'object') {
          jsonString = JSON.stringify(template.json, null, 2);
        }
      }
      if (!jsonString) throw new Error("No JSON data found in template");
      
      // Parse JSON to check format
      const parsedJson = JSON.parse(jsonString);
      
      // Extract unique variables for form state
      const vars = extractUniqueVariables(jsonString);
      setUniqueVariables(vars);
      
      // Replace variables with database data (initial values)
      const processedJsonString = await replaceVariablesWithData(jsonString);
      setProcessedJson(processedJsonString);
      
      // Convert to HTML
      const result = convertJsonToHtml(processedJsonString);
      if (result.success) {
        // Note: Signature processing is now handled by the backend
        // const htmlWithSignatures = processSignaturePlaceholders(result.html);
        setHtmlContent(result.html);
        // Generate form HTML
        const formHtmlContent = generateFormHtml(parsedJson, {}, handleInputChange);
        setFormHtml(formHtmlContent);
        // Set initial form values from placeholder data
        const initialValues = {};
        for (const v of vars) {
          initialValues[v] = await getPlaceholderData(v);
        }
        setFormValues(initialValues);
      } else {
        throw new Error(result.message || "Failed to convert JSON to HTML");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Generate form HTML with current values and onChange handlers
  const generateFormHtmlWithValues = (jsonData, values = {}, onChange) => {
    const jsonString = JSON.stringify(jsonData);
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables = [...jsonString.matchAll(variableRegex)];
    let formHtml = '<div class="form-container" style="max-width: 800px; margin: 0 auto; padding: 20px;">';
    formHtml += `\n    <div style="text-align: center; margin-bottom: 30px;">\n      <img src="${fdLogo}" alt="Fisher Dugmore Financial" style="width: 120px; margin-bottom: 20px;" />\n      <h2 style="margin: 0; text-transform: uppercase; letter-spacing: 2px;">Template Form</h2>\n    </div>\n  `;
    const uniqueVars = [...new Set(variables.map(v => v[1].trim()))];
    uniqueVars.forEach(variableName => {
      const elementType = VARIABLE_ELEMENT_MAP[variableName] || 'text';
      const displayName = variableName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const value = values[variableName] || '';
      formHtml += `<div style="margin-bottom: 20px;">`;
      formHtml += `<label style="display: block; margin-bottom: 5px; font-weight: bold; color: #333;">${displayName}:</label>`;
      switch (elementType) {
        case 'textarea':
          formHtml += `<textarea name="${variableName}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-family: inherit; resize: vertical;" rows="3" placeholder="Enter ${displayName.toLowerCase()}..." oninput="window.handleInputChange && window.handleInputChange(event)">${value}</textarea>`;
          break;
        case 'select':
          formHtml += `<select name="${variableName}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-family: inherit;" onchange="window.handleInputChange && window.handleInputChange(event)">\n          <option value="">Select ${displayName}</option>\n          <option value="option1">Option 1</option>\n          <option value="option2">Option 2</option>\n          <option value="option3">Option 3</option>\n        </select>`;
          break;
        case 'checkbox':
          formHtml += `<label style="display: flex; align-items: center; font-weight: normal;">\n          <input type="checkbox" name="${variableName}" style="margin-right: 8px;" ${value ? 'checked' : ''} onchange="window.handleInputChange && window.handleInputChange(event)" />\n          ${displayName}\n        </label>`;
          break;
        default:
          formHtml += `<input type="${elementType}" name="${variableName}" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; font-family: inherit;" placeholder="Enter ${displayName.toLowerCase()}..." value="${value}" oninput="window.handleInputChange && window.handleInputChange(event)" />`;
      }
      formHtml += `</div>`;
    });
    formHtml += '</div>';
    return formHtml;
  };

  // Replace variables in the PDF HTML with form values
  const getPdfHtmlWithValues = () => {
    let html = htmlContent;
    uniqueVariables.forEach(variable => {
      const value = formValues[variable] || '';
      const regex = new RegExp(`{{\\s*${variable}\\s*}}`, 'g');
      html = html.replace(regex, value);
    });
    // Note: Signature processing is now handled by the backend
    // return processSignaturePlaceholders(html);
    return html;
  };

  const handleClose = () => {
    setActiveTab(0);
    setProcessedJson("");
    setHtmlContent("");
    setFormHtml("");
    setError(null);
    onClose();
  };

  const renderHtmlView = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
          <MDTypography variant="body2">Processing template...</MDTypography>
        </MDBox>
      );
    }
    if (error) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
          <MDTypography variant="body2" color="error">
            Error: {error}
          </MDTypography>
        </MDBox>
      );
    }
    // Render form using React elements for interactivity
    return (
      <MDBox>
        <MDBox mb={2}>
          <MDTypography variant="h6" color="info">
            Interactive Form View
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Form fields with database values pre-populated
          </MDTypography>
        </MDBox>
        <form style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 24, background: '#fafafa', maxHeight: '70vh', overflow: 'auto' }}>
          {uniqueVariables.map(variableName => {
            const elementType = VARIABLE_ELEMENT_MAP[variableName] || 'text';
            const displayName = variableName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
            const value = formValues[variableName] || '';
            return (
              <div key={variableName} style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 'bold', color: '#333' }}>{displayName}:</label>
                {elementType === 'textarea' ? (
                  <textarea
                    name={variableName}
                    style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'inherit', resize: 'vertical' }}
                    rows={3}
                    placeholder={`Enter ${displayName.toLowerCase()}...`}
                    value={value}
                    onChange={handleInputChange}
                  />
                ) : elementType === 'select' ? (
                  <select
                    name={variableName}
                    style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'inherit' }}
                    value={value}
                    onChange={handleInputChange}
                  >
                    <option value="">Select {displayName}</option>
                    <option value="option1">Option 1</option>
                    <option value="option2">Option 2</option>
                    <option value="option3">Option 3</option>
                  </select>
                ) : elementType === 'checkbox' ? (
                  <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                    <input
                      type="checkbox"
                      name={variableName}
                      style={{ marginRight: 8 }}
                      checked={!!value}
                      onChange={handleInputChange}
                    />
                    {displayName}
                  </label>
                ) : (
                  <input
                    type={elementType}
                    name={variableName}
                    style={{ width: '100%', padding: 10, border: '1px solid #ddd', borderRadius: 4, fontFamily: 'inherit' }}
                    placeholder={`Enter ${displayName.toLowerCase()}...`}
                    value={value}
                    onChange={handleInputChange}
                  />
                )}
              </div>
            );
          })}
        </form>
      </MDBox>
    );
  };

  const renderPdfView = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
          <MDTypography variant="body2">Processing template...</MDTypography>
        </MDBox>
      );
    }
    if (error) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" py={4}>
          <MDTypography variant="body2" color="error">
            Error: {error}
          </MDTypography>
        </MDBox>
      );
    }
    // Replace variables in the HTML with current form values
    const pdfHtml = getPdfHtmlWithValues();
    return (
      <MDBox>
        <MDBox mb={2}>
          <MDTypography variant="h6" color="info">
            PDF Preview
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Document preview with current form values
          </MDTypography>
        </MDBox>
        <MDBox
          sx={{
            background: '#f5f5f5',
            p: 3,
            borderRadius: 2,
            maxHeight: '70vh',
            overflow: 'auto'
          }}
        >
          <div style={{
            background: '#fff',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10)',
            borderRadius: 8,
            margin: '0 auto',
            padding: '48px 56px',
            maxWidth: 800,
            minHeight: 600,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <img src={fdLogo} alt="Fisher Dugmore Financial Logo" style={{ width: 120, marginBottom: 24 }} />
            <h2 style={{ 
              textTransform: 'uppercase', 
              margin: 0, 
              marginBottom: 32, 
              letterSpacing: 2, 
              fontWeight: 700, 
              fontSize: 24, 
              textAlign: 'center' 
            }}>
              {template?.name || 'TEMPLATE NAME'}
            </h2>
            <div 
              style={{ width: '100%', textAlign: 'left' }}
              dangerouslySetInnerHTML={{ __html: pdfHtml }}
            />
          </div>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          height: '90vh',
          maxWidth: '90vw',
          maxHeight: '90vh',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h5">
              {template?.name || 'Template View'}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Template with database values
            </MDTypography>
          </MDBox>
          <MDButton
            variant="text"
            color="dark"
            onClick={handleClose}
            startIcon={<Icon>close</Icon>}
          >
            Close
          </MDButton>
        </MDBox>
      </DialogTitle>
      <DialogContent sx={{ height: '100%', p: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab label="HTML Form View" />
          <Tab label="PDF Preview" />
        </Tabs>
        
        {activeTab === 0 && renderHtmlView()}
        {activeTab === 1 && renderPdfView()}
      </DialogContent>
    </Dialog>
  );
}

TemplateViewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  template: PropTypes.object,
};

export default TemplateViewModal; 